// /admin/sources — operational dashboard for the 8 ingestion adapters.
//
// Per BUILD.md §13: shows last run / last success / last error / consecutive
// failures / schedule / enabled toggle / "Run now" per source. Auth-gated to
// admin role on the API; the page also routes unauthenticated viewers to the
// login screen.

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatDate } from '../lib/format';

interface SourceRow {
  id: string;
  name: string;
  adapter: string;
  start_url: string;
  schedule_cron: string;
  enabled: 0 | 1;
  last_run_at: number | null;
  last_success_at: number | null;
  last_error: string | null;
  consecutive_failures: number;
  last_url_check_ok_at: number | null;
  recent_self_heal: {
    occurred_at: number;
    before_url: string | null;
    after_url: string | null;
    method: string | null;
    keywords_matched: string | null;
  } | null;
}

// Classify last_error by prefix so url-layer issues read differently from
// parser failures. The prefixes are written by src/ingestion/health_check.ts.
export function classifyError(err: string | null): {
  kind: 'none' | 'health_failed' | 'scrape_warn' | 'parser';
  badge: string | null;
  badgeClass: string;
} {
  if (!err) return { kind: 'none', badge: null, badgeClass: '' };
  if (err.startsWith('url_health:')) {
    return {
      kind: 'health_failed',
      badge: 'URL unreachable',
      badgeClass: 'bg-amber-100 text-amber-900',
    };
  }
  if (err.startsWith('scrape_warn:')) {
    // Warning, not failure: the run succeeded but found nothing — usually a
    // broken selector after a funder site redesign (src/ingestion/pipeline.ts).
    return {
      kind: 'scrape_warn',
      badge: 'Zero results',
      badgeClass: 'bg-amber-100 text-amber-900',
    };
  }
  return {
    kind: 'parser',
    badge: 'Parser error',
    badgeClass: 'bg-rose-100 text-rose-900',
  };
}

// Adapters that, by design, only emit a single dataset-level signpost
// record rather than per-program grants. Surfacing this on the admin page
// so an operator doesn't read "1 program found" as a regression vs. the
// other adapters that surface dozens.
//
// canada_open_data: CKAN dataset is retrospective awards, not prospective
// programs — see src/ingestion/adapters/canada_open_data.ts for the full
// rationale. Per-program extraction is deferred to a future step.
const SIGNPOST_ADAPTERS: Record<string, string> = {
  canada_open_data:
    'Signpost mode: emits one dataset-level record (proactive disclosure is retrospective awards, not prospective grant programs). Per-program extraction is deferred — see adapter source for rationale.',
};

interface RunResult {
  source_id: string;
  run_id: string;
  found: number;
  new_programs: number;
  skipped: number;
  failed: number;
  null_parsed: number;
}

export default function AdminSourcesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<{ id: string; msg: string; kind: 'ok' | 'err' } | null>(null);

  const sources = useQuery({
    queryKey: ['admin', 'sources'],
    queryFn: () => api.get<SourceRow[]>('/admin/sources'),
    refetchInterval: 15_000, // keep last_run / last_error fresh while ops are watching
  });

  const runNow = useMutation({
    mutationFn: (id: string) => api.post<RunResult>(`/admin/sources/${id}/run-now`),
    onSuccess: async (res) => {
      const d = res.data;
      setFeedback({
        id: d.source_id,
        kind: 'ok',
        msg: `Run ${d.run_id} — found ${d.found}, new ${d.new_programs}, skipped ${d.skipped}, failed ${d.failed}, null ${d.null_parsed}`,
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'sources'] });
    },
    onError: (err, id) => {
      setFeedback({
        id,
        kind: 'err',
        msg: err instanceof ApiError ? `${err.code}: ${err.message}` : String(err),
      });
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<SourceRow>(`/admin/sources/${id}`, { enabled }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'sources'] });
    },
    onError: (err, vars) => {
      setFeedback({
        id: vars.id,
        kind: 'err',
        msg: err instanceof ApiError ? `${err.code}: ${err.message}` : String(err),
      });
    },
  });

  if (sources.isLoading) return <Padded>Loading sources…</Padded>;
  if (sources.error instanceof ApiError) {
    if (sources.error.status === 401) {
      navigate({ to: '/login' });
      return null;
    }
    if (sources.error.status === 403) {
      return <Padded>This page requires the admin role.</Padded>;
    }
    return <Padded>Error: {sources.error.message}</Padded>;
  }
  const rows = sources.data?.data ?? [];

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <h1 className="text-xl font-semibold text-neutral-900">Ingestion sources</h1>
      <p className="text-sm text-neutral-600 mt-1">
        Eight adapters covering federal, provincial, regional and Indigenous funders.
        Toggle <span className="font-medium">Enabled</span> to include a source in the next cron run;
        use <span className="font-medium">Run now</span> to trigger a manual ingestion that returns counts inline.
      </p>

      {feedback && (
        <div
          role={feedback.kind === 'err' ? 'alert' : 'status'}
          aria-live="polite"
          className={
            'mt-3 text-sm rounded-md p-2 border ' +
            (feedback.kind === 'err'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800')
          }
        >
          <span className="font-mono text-xs text-neutral-500 mr-2">{feedback.id}</span>{feedback.msg}
        </div>
      )}

      <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-600">
              <th scope="col" className="px-3 py-2">Source</th>
              <th scope="col" className="px-3 py-2">Adapter</th>
              <th scope="col" className="px-3 py-2">Schedule</th>
              <th scope="col" className="px-3 py-2">Last run</th>
              <th scope="col" className="px-3 py-2">Last success</th>
              <th scope="col" className="px-3 py-2">Failures</th>
              <th scope="col" className="px-3 py-2">Enabled</th>
              <th scope="col" className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isRunning = runNow.isPending && runNow.variables === r.id;
              const errClass = classifyError(r.last_error);
              const heal = r.recent_self_heal;
              return (
                <tr key={r.id} className="border-t border-neutral-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900 flex flex-wrap items-center gap-2">
                      <span>{r.name}</span>
                      {SIGNPOST_ADAPTERS[r.adapter] && (
                        <span
                          className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900"
                          title={SIGNPOST_ADAPTERS[r.adapter]}
                          aria-label={`Signpost mode: ${SIGNPOST_ADAPTERS[r.adapter]}`}
                        >
                          Signpost mode
                        </span>
                      )}
                      {errClass.badge && (
                        <span
                          className={
                            'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ' +
                            errClass.badgeClass
                          }
                          aria-label={`${errClass.badge}: see Last error below`}
                        >
                          {errClass.badge}
                        </span>
                      )}
                      {heal && (
                        <span
                          className="inline-flex items-center rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-900"
                          title={
                            `Self-healed ${formatDate(heal.occurred_at)}\n` +
                            `from: ${heal.before_url ?? '—'}\n` +
                            `to:   ${heal.after_url ?? '—'}\n` +
                            `method: ${heal.method ?? '—'}` +
                            (heal.keywords_matched ? `\nkeywords: ${heal.keywords_matched}` : '')
                          }
                          aria-label={
                            `Recently self-healed on ${formatDate(heal.occurred_at)}, ` +
                            `from ${heal.before_url ?? 'unknown'} to ${heal.after_url ?? 'unknown'}, ` +
                            `method ${heal.method ?? 'unknown'}`
                          }
                        >
                          Recently self-healed
                        </span>
                      )}
                    </div>
                    {SIGNPOST_ADAPTERS[r.adapter] && (
                      <p className="mt-1 text-xs text-amber-900/80 italic">
                        {SIGNPOST_ADAPTERS[r.adapter]}
                      </p>
                    )}
                    <a
                      href={r.start_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-xs text-accent underline break-all"
                    >{r.start_url}</a>
                    {r.last_error && (
                      <details className="mt-1 text-xs">
                        <summary
                          className={
                            'cursor-pointer ' +
                            (errClass.kind === 'health_failed' ? 'text-amber-800' : 'text-rose-700')
                          }
                        >Last error</summary>
                        <blockquote
                          className={
                            'mt-1 font-mono whitespace-pre-wrap border-l-2 pl-2 ' +
                            (errClass.kind === 'health_failed'
                              ? 'border-amber-300 text-amber-900'
                              : 'border-rose-300 text-rose-800')
                          }
                        >
                          {r.last_error}
                        </blockquote>
                      </details>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.adapter}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.schedule_cron}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.last_run_at)}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.last_success_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={r.consecutive_failures > 0 ? 'text-rose-700 font-medium' : 'text-neutral-700'}>
                      {r.consecutive_failures}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.enabled === 1}
                        disabled={toggle.isPending}
                        onChange={(e) => toggle.mutate({ id: r.id, enabled: e.target.checked })}
                        aria-label={`Enable ingestion source ${r.name}`}
                      />
                      <span className="text-xs">{r.enabled === 1 ? 'On' : 'Off'}</span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => runNow.mutate(r.id)}
                      disabled={isRunning}
                      className="px-2 py-1 rounded-md border border-neutral-300 text-xs hover:bg-neutral-50 disabled:opacity-60"
                    >
                      {isRunning ? 'Running…' : 'Run now'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-sm text-neutral-600">
                  No ingestion sources are registered yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Padded({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-sm text-neutral-700">{children}</div>;
}
